import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";

import { useAuthStore } from "../stores/authStore";
import { refreshSessionTokens } from "./tokenRefresh";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "/api";

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
});

apiClient.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

type RetryableRequestConfig = InternalAxiosRequestConfig & { _retry?: boolean };

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const axiosError = error as AxiosError;
    const statusCode = axiosError.response?.status;
    const originalRequest = axiosError.config as RetryableRequestConfig | undefined;

    if (statusCode === 401 && originalRequest && !originalRequest._retry) {
      const currentRefreshToken = useAuthStore.getState().refreshToken;
      const requestUrl = originalRequest.url ?? "";

      if (!currentRefreshToken || requestUrl.includes("/psc/auth/refresh/") || requestUrl.includes("/psc/auth/token/refresh/")) {
        useAuthStore.getState().clearSession();
        return Promise.reject(error);
      }

      originalRequest._retry = true;
      try {
        const nextTokens = await refreshSessionTokens(currentRefreshToken);
        const nextAccessToken = nextTokens.access;
        originalRequest.headers.Authorization = `Bearer ${nextAccessToken}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        useAuthStore.getState().clearSession();
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  },
);
